import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Field, Input } from '../components/Field';
import { Avatar } from '../components/Avatar';
import { Pill } from '../components/Pill';
import { PasswordStrengthMeter, usePasswordRules } from '../components/PasswordStrengthMeter';
import { PushSettings } from '../components/PushSettings';
import { authApi } from '../api/auth';
import { useAuth } from '../store/auth';
import { formatDate } from '../lib/date';
import type { HttpError } from '../api/client';

export function ProfilePage() {
  const cachedUser = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);

  const me = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  useEffect(() => {
    if (me.data) setUser(me.data);
  }, [me.data, setUser]);

  const user = me.data ?? cachedUser;

  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { allValid: passwordValid } = usePasswordRules(newPassword);

  const change = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setCurrent('');
      setNew('');
      setConfirm('');
      setSuccess(true);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setConfirmError(null);
    setSuccess(false);
    if (!passwordValid) {
      setConfirmError('Nowe hasło nie spełnia wszystkich wymagań.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmError('Powtórzone hasło nie pasuje.');
      return;
    }
    change.mutate();
  };

  if (!user) return <DesktopShell><TopBar title="Profil" /></DesktopShell>;

  const apiErr = change.isError ? (change.error as HttpError).body?.message ?? 'Nie udało się zmienić hasła.' : null;

  return (
    <DesktopShell>
      <TopBar title="Profil" subtitle="Twoje dane konta i hasło" />
      <div
        className="stack-on-mobile"
        style={{
          flex: 1,
          padding: '28px 36px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <Card padding={24}>
          <h3 className="serif" style={{ margin: 0, fontSize: 18, fontWeight: 700, marginBottom: 18 }}>
            Twoje dane
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22 }}>
            <Avatar nick={user.nick} size={64} />
            <div style={{ minWidth: 0 }}>
              <div className="serif" style={{ fontSize: 22, fontWeight: 700 }}>
                {user.nick}
              </div>
              <div style={{ fontSize: 13, color: '#5b6578', marginTop: 2, overflowWrap: 'anywhere' }}>
                {user.email}
              </div>
            </div>
          </div>
          <DataRow label="Nick" value={user.nick} />
          <DataRow
            label="Rola"
            value={
              user.role === 'ADMIN' ? (
                <Pill color="accent">ADMIN</Pill>
              ) : (
                <Pill color="gray">UŻYTKOWNIK</Pill>
              )
            }
          />
          <DataRow label="Konto od" value={user.createdAt ? formatDate(user.createdAt) : '—'} />
          <DataRow label="ID" value={<span className="mono" style={{ fontSize: 12 }}>{user.id}</span>} />
        </Card>

        <Card padding={24}>
          <PushSettings />
        </Card>

        <Card padding={24}>
          <h3 className="serif" style={{ margin: 0, fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
            Zmień hasło
          </h3>
          <div style={{ fontSize: 13, color: '#5b6578', marginBottom: 18 }}>
            Wybierz nowe hasło spełniające wymagania bezpieczeństwa.
          </div>
          <form onSubmit={onSubmit}>
            <Field label="Aktualne hasło">
              <Input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            </Field>
            <Field label="Nowe hasło">
              <Input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNew(e.target.value)}
                required
                minLength={12}
              />
            </Field>
            <div style={{ marginTop: -6, marginBottom: 14 }}>
              <PasswordStrengthMeter password={newPassword} />
            </div>
            <Field label="Powtórz nowe hasło" error={confirmError ?? undefined}>
              <Input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </Field>

            {success && (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 14,
                  padding: 12,
                  background: 'rgba(31,157,85,.1)',
                  color: '#0f6b34',
                  borderRadius: 10,
                  fontSize: 13,
                }}
              >
                Hasło zostało zmienione.
              </div>
            )}
            {apiErr && (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 14,
                  padding: 12,
                  background: 'rgba(208,70,60,.08)',
                  color: '#8a2a22',
                  borderRadius: 10,
                  fontSize: 13,
                }}
              >
                {apiErr}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Btn
                type="submit"
                variant="primary"
                size="md"
                disabled={
                  change.isPending ||
                  !currentPassword ||
                  !passwordValid ||
                  newPassword !== confirmPassword
                }
              >
                {change.isPending ? 'Zapisywanie…' : 'Zmień hasło'}
              </Btn>
            </div>
          </form>
        </Card>
      </div>
    </DesktopShell>
  );
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px minmax(0, 1fr)',
        gap: 12,
        padding: '10px 0',
        borderTop: '1px solid rgba(15,30,61,.06)',
        alignItems: 'center',
      }}
    >
      <div style={{ fontSize: 11, color: '#5b6578', fontWeight: 600, letterSpacing: '.06em' }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 14, color: '#0f1e3d', overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}
